/**
 * Shared JavaScript functionality for Netwrck AI Tools
 */
(function () {
  'use strict';
  try {
    import('./safe-bootstrap.js');
  } catch { /* Error ignored */ }

  // Global tools utilities
  window.NetwrckTools = window.NetwrckTools || {};

  function shouldRedactToolLogKey(key) {
    const normalized = String(key || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return (
      normalized === 'secret' ||
      normalized === 'apikey' ||
      normalized === 'clientsecret' ||
      normalized === 'sessionid' ||
      normalized === 'authorization'
    );
  }

  function redactToolLogValue(value, seen) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^[\[{]/.test(trimmed)) {
        try {
          return JSON.stringify(redactToolLogValue(JSON.parse(trimmed), seen));
        } catch (_) {}
      }
      if (/^(sk_|cs_)/.test(value)) return '[redacted]';
      return value
        .replace(
          /((?:client_secret|clientSecret|api_key|apiKey|secret)=)[^&\s,}]+/g,
          '$1[redacted]'
        )
        .replace(
          /((?:client_secret|clientSecret|api_key|apiKey|secret)["']?\s*:\s*["'])[^"']+/g,
          '$1[redacted]'
        );
    }
    if (value instanceof Error) {
      return {
        name: value.name,
        message: redactToolLogValue(value.message, seen),
      };
    }
    if (!value || typeof value !== 'object') return value;
    const visited = seen || new WeakSet();
    if (visited.has(value)) return '[circular]';
    visited.add(value);
    if (Array.isArray(value)) {
      return value.map((item) => redactToolLogValue(item, visited));
    }
    const copy = {};
    Object.keys(value).forEach((key) => {
      copy[key] = shouldRedactToolLogKey(key)
        ? '[redacted]'
        : redactToolLogValue(value[key], visited);
    });
    return copy;
  }

  function toolsDebug() {
    let enabled = false;
    try {
      enabled =
        Boolean(window.DEBUG || window.__TOOLS_DEBUG__) ||
        localStorage.getItem('netwrck_tools_debug') === '1';
    } catch (_) {}
    if (!enabled) return;
    console.log.apply(
      console,
      Array.prototype.map.call(arguments, (arg) => redactToolLogValue(arg))
    );
  }

  function toolsWarn() {
    console.warn.apply(
      console,
      Array.prototype.map.call(arguments, (arg) => redactToolLogValue(arg))
    );
  }

  function toolsError() {
    console.error.apply(
      console,
      Array.prototype.map.call(arguments, (arg) => redactToolLogValue(arg))
    );
  }

  /**
   * Common initialization for all tools
   */
  function initializeCommonFeatures() {
    // Initialize Stripe for payments
    NetwrckTools.stripe = null;

    // Wait for dependencies to load
    if (typeof jQuery === 'undefined' || typeof $ === 'undefined') {
      setTimeout(initializeCommonFeatures, 100);
      return;
    }

    $(document).ready(function () {
      initializeStripePayments();
      setupCommonEventHandlers();
      ensureUserData();
      updateBalanceDisplay();
      checkPaymentStatus();
    });
  }

  /**
   * Initialize Stripe payment system
   */
  function initializeStripePayments() {
    if (typeof window.Stripe !== 'undefined' && !NetwrckTools.stripe) {
      fetch('/api/stripe-config')
        .then((response) => {
          if (response.status === 404) {
            toolsWarn(
              'Stripe config endpoint not found - Stripe functionality disabled'
            );
            return null;
          }
          return response.json();
        })
        .then((data) => {
          if (data && data.publishable_key) {
            NetwrckTools.stripe = window.Stripe(data.publishable_key);
            toolsDebug('Stripe initialized successfully for tools');
          }
        })
        .catch((error) => {
          toolsWarn('Stripe configuration not available:', error.message);
        });
    } else if (typeof window.Stripe === 'undefined') {
      setTimeout(initializeStripePayments, 500);
    }
  }

  function ensureUserData() {
    try {
      if (typeof window.userData === 'undefined' || window.userData === null) {
        window.userData = {};
      }
    } catch { /* Error ignored */ }
    if (window.userData && window.userData.id && window.userData.secret) {
      $(document).trigger('user-ready', [window.userData]);
      refreshUserData();
      return;
    }
    refreshUserData();
  }

  function refreshUserData() {
    if (typeof fetch !== 'function') return;
    const req = fetch('/api/current-user', { credentials: 'include' });
    if (!req || typeof req.then !== 'function') return;
    req
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.success && data.logged_in && data.user) {
          const updated = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.name || data.user.username || data.user.email,
            username: data.user.username,
            secret: data.user.secret,
            is_subscribed: !!data.user.is_subscribed,
            free_credits: data.user.free_credits || 0,
            paid_credits: data.user.paid_credits || 0,
            credits: data.user.credits || 0,
            avatar_photo_url: data.user.avatar_photo_url,
          };
          if (!updated.secret) {
            try {
              if (window.getAuthCredentials) {
                const creds = window.getAuthCredentials();
                if (creds && creds.secret) updated.secret = creds.secret;
              }
              if (!updated.secret) {
                const ls = localStorage.getItem('userSecret');
                if (ls) updated.secret = ls;
              }
              if (!updated.secret && document && document.cookie) {
                const cookies = document.cookie.split(';').reduce((a, c) => {
                  const [k, v] = c.trim().split('=');
                  a[k] = v;
                  return a;
                }, {});
                if (cookies.user_secret) updated.secret = cookies.user_secret;
              }
            } catch { /* Error ignored */ }
          }
          window.userData = Object.assign({}, window.userData || {}, updated);
          try {
            localStorage.setItem('netwrck_user', JSON.stringify(window.userData));
            localStorage.setItem('userData', JSON.stringify(window.userData));
          } catch { /* Error ignored */ }
          updateBalanceDisplay();
          $(document).trigger('user-ready', [window.userData]);
        }
      })
      .catch(() => {});
  }

  /**
   * Update balance display across tools
   */
  function updateBalanceDisplay() {
    if (window.userData) {
      var totalCredits =
        parseFloat(window.userData.free_credits || 0) +
        parseFloat(window.userData.paid_credits || 0);
      if (!totalCredits && window.userData.credits !== undefined) {
        totalCredits = parseFloat(window.userData.credits || 0);
      }
      $('.api-credits-balance-usd').text(totalCredits.toFixed(2));
      $('.api-credits-balance').text(totalCredits);
      $('.credits-value').text(totalCredits.toFixed(2));
    }
  }

  /**
   * Check for payment completion status
   */
  function checkPaymentStatus() {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');

    if (paymentStatus === 'success') {
      if (window.userData && window.userData.id) {
        fetch('/api/get-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid: window.userData.id,
            secret: window.userData.secret,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.user) {
              window.userData.free_credits = data.user.free_credits;
              window.userData.paid_credits = data.user.paid_credits;
              updateBalanceDisplay();
              if (typeof window.showToast === 'function') {
                window.showToast('Payment successful! Your credits have been added.', { type: 'success', timeout: 5000 });
              } else {
                alert('Payment successful! Your credits have been added.');
              }
            }
          })
          .catch((error) => toolsError('Error updating balance:', error));
      }

      const url = new URL(window.location);
      url.searchParams.delete('payment');
      window.history.replaceState({}, '', url);
    } else if (paymentStatus === 'cancel') {
      if (typeof window.showToast === 'function') {
        window.showToast('Payment was canceled.', { type: 'warning' });
      } else {
        alert('Payment was canceled.');
      }
      const url = new URL(window.location);
      url.searchParams.delete('payment');
      window.history.replaceState({}, '', url);
    }
  }

  /**
   * Show topup credits modal
   */
  NetwrckTools.showTopupCreditsModal = function () {
    // Prefer global helper (provides robust fallbacks to embedded checkout)
    if (
      typeof window.showTopupCreditsModal === 'function' &&
      window.showTopupCreditsModal !== NetwrckTools.showTopupCreditsModal
    ) {
      try {
        window.showTopupCreditsModal();
        return;
      } catch { /* Error ignored; fall back below */ }
    }

    // Legacy dialog flow
    var dialog = document.getElementById('topup-credits-dialog');
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
      if (typeof componentHandler !== 'undefined') {
        componentHandler.upgradeElements(
          dialog.querySelectorAll('.mdl-radio, .mdl-textfield')
        );
      }
      return;
    }

    // Final fallback: open embedded checkout directly if available
    if (window.EmbeddedCheckout && typeof window.EmbeddedCheckout.open === 'function') {
      try {
        window.EmbeddedCheckout.open({ type: 'credits' });
        return;
      } catch { /* Error ignored */ }
    }

    if (window.location && window.location.pathname !== '/account') {
      window.location.href = '/account';
    }
  };

  /**
   * Show credits notification as a clean white modal with brand button
   * Ensures only a single instance (no duplicates)
   */
  NetwrckTools.showCreditsToast = function (requiredCredits, costInUSD) {
    // Remove any legacy toast container if present to avoid double-up
    var legacy = document.getElementById('credits-toast-container');
    if (legacy && legacy.parentNode) legacy.parentNode.removeChild(legacy);

    // Reuse existing dialog if present
    var dialog = document.getElementById('insufficient-credits-dialog');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'insufficient-credits-dialog';
      dialog.style.cssText = [
        'max-width: 520px',
        'width: calc(100% - 40px)',
        'border: none',
        'border-radius: 8px',
        'padding: 0',
        'box-shadow: 0 10px 30px rgba(0,0,0,0.25)',
        'background: #fff',
        'color: #111',
        'overflow: hidden',
      ].join(';');

      dialog.innerHTML = `
        <div class="icd-container" style="padding:0;">
          <div class="icd-header" style="
              display:flex; align-items:center; justify-content:space-between;
              padding:12px 16px; border-bottom:1px solid #eee; background:#f5f5f5;">
            <div style="display:flex; align-items:center; gap:8px;">
              <i class="material-icons" style="color:#ff9800;">account_balance_wallet</i>
              <div id="icd-title" style="font-weight:600; font-size:16px;">Insufficient credits</div>
            </div>
            <button id="icd-close" class="mdl-button mdl-js-button mdl-button--icon" aria-label="Close">
              <i class="material-icons">close</i>
            </button>
          </div>
          <div class="icd-body" style="padding:16px 20px;">
            <div id="icd-message" style="font-size:14px; line-height:1.5; color:#444;"></div>
            <div style="display:flex; gap:10px; margin-top:16px;">
              <button id="icd-topup" class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored mdl-js-ripple-effect">Top Up</button>
            </div>
          </div>
        </div>`;

      // Wire actions
      (function(){
        var topupBtn = dialog.querySelector('#icd-topup');
        if (topupBtn) {
          topupBtn.addEventListener('click', function () {
            dialog.close();
            if (window.EmbeddedCheckout && typeof window.EmbeddedCheckout.open === 'function') {
              try { window.EmbeddedCheckout.open({ amount: 25, type: 'credits' }); return; } catch { /* Error ignored */ }
            }
            if (typeof window.showTopupDialog === 'function') {
              try { window.showTopupDialog(); return; } catch { /* Error ignored */ }
            }
            if (typeof window.showTopupCreditsModal === 'function') {
              try { window.showTopupCreditsModal(); return; } catch { /* Error ignored */ }
            }
            NetwrckTools.showTopupCreditsModal();
          });
        }
        var closeBtn = dialog.querySelector('#icd-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            try { dialog.close(); } catch { /* Error ignored */ }
          });
        }
        try {
          dialog.addEventListener('click', function (e) {
            if (e.target === dialog) { try { dialog.close(); } catch { /* Error ignored */ } }
          });
        } catch { /* Error ignored */ }
        try {
          dialog.addEventListener('cancel', function (e) {
            try { e.preventDefault(); } catch { /* Error ignored */ }
            try { dialog.close(); } catch { /* Error ignored */ }
          });
        } catch { /* Error ignored */ }
        if (typeof componentHandler !== 'undefined') {
          try {
            componentHandler.upgradeElements(
              dialog.querySelectorAll('.mdl-button, .mdl-js-button')
            );
          } catch { /* Error ignored */ }
        }
      })();

      document.body.appendChild(dialog);
    }

    // Update dynamic message and open
    var msg = dialog.querySelector('#icd-message');
    if (msg) {
      msg.textContent = `This operation costs ${requiredCredits} credits ($${costInUSD}). Please top up to continue.`;
    }

    try {
      dialog.showModal();
    } catch (_) {
      // Fallback for environments without <dialog> polyfill
      dialog.setAttribute('open', 'open');
    }
  };

  /**
   * Purchase credits with amount
   */
  NetwrckTools.purchaseCredits = function (amount) {
    if (!window.userData || !window.userData.id) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please log in to purchase credits', { type: 'warning' });
      } else {
        alert('Please log in to purchase credits');
      }
      return;
    }

    const dialog = document.getElementById('topup-credits-dialog');
    if (dialog) dialog.close();

    if (NetwrckTools.stripe) {
      toolsDebug(`Purchasing ${amount} credits with embedded checkout`);
      createEmbeddedCheckout(amount)
        .then((clientSecret) => {
          if (clientSecret) {
            return showEmbeddedCheckout(clientSecret);
          }
        })
        .catch((error) => {
          toolsError('Error with embedded checkout:', error);
          fallbackToHostedCheckout(amount);
        });
    } else {
      toolsDebug(`Purchasing ${amount} credits with hosted checkout`);
      fallbackToHostedCheckout(amount);
    }
  };

  /**
   * Create embedded checkout session
   */
  function createEmbeddedCheckout(amount) {
    if (!window.userData || !window.userData.id) {
      return Promise.reject('User not authenticated');
    }

    const purchaseData = {
      uid: window.userData.id,
      secret: window.userData.secret,
      amount: amount,
      embedded: true,
    };

    const headers = { 'Content-Type': 'application/json' };
    if (window.userData && window.userData.secret) {
      headers['Authorization'] = `Bearer ${window.userData.secret}`;
      headers['X-API-Key'] = window.userData.secret;
    }

    return fetch('/api/create-credits-checkout', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(purchaseData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success && data.client_secret) {
          if (
            data.client_secret.startsWith('cs_') &&
            data.client_secret.includes('_secret_')
          ) {
            return data.client_secret;
          } else {
            throw new Error('Invalid client secret format');
          }
        } else if (data.success && data.url) {
          window.location.href = data.url;
          return null;
        } else {
          throw new Error(data.error || 'Failed to create checkout session');
        }
      });
  }

  /**
   * Show embedded checkout modal
   */
  function showEmbeddedCheckout(clientSecret) {
    if (!NetwrckTools.stripe) {
      toolsError('Stripe not initialized');
      if (typeof window.showToast === 'function') {
        window.showToast('Payment system not ready. Please try again in a moment.', { type: 'error' });
      } else {
        alert('Payment system not ready. Please try again in a moment.');
      }
      return;
    }

    // Proactively destroy any existing embedded checkout instances to avoid duplicates
    if (
      window.EmbeddedCheckout &&
      typeof window.EmbeddedCheckout.destroyAll === 'function'
    ) {
      try {
        window.EmbeddedCheckout.destroyAll();
      } catch { /* Error ignored */ }
    }

    const checkoutElement = NetwrckTools.stripe
      .elements({
        clientSecret: clientSecret,
      })
      .create('embedded-checkout');

    // Share element so the central module can unmount on cleanup
    if (
      window.EmbeddedCheckout &&
      typeof window.EmbeddedCheckout._setExternalElement === 'function'
    ) {
      try {
        window.EmbeddedCheckout._setExternalElement(checkoutElement);
      } catch { /* Error ignored */ }
    }

    const modal = document.getElementById('stripe-checkout-modal');
    if (!modal) {
      toolsError('Checkout modal not found');
      return;
    }

    const loading = modal.querySelector('#credits-checkout-loading');
    if (loading) loading.style.display = 'none';

    const checkoutContainer = modal.querySelector('#credits-checkout-element');
    checkoutContainer.innerHTML = '';
    checkoutElement.mount('#credits-checkout-element');

    modal.showModal();

    const closeBtn = modal.querySelector('.close-checkout');
    if (closeBtn) {
      closeBtn.onclick = () => {
        modal.close();
        checkoutElement.unmount();
        if (
          window.EmbeddedCheckout &&
          typeof window.EmbeddedCheckout._setExternalElement === 'function'
        ) {
          try {
            window.EmbeddedCheckout._setExternalElement(null);
          } catch { /* Error ignored */ }
        }
      };
    }

    return checkoutElement;
  }

  /**
   * Fallback to hosted Stripe checkout
   */
  function fallbackToHostedCheckout(amount) {
    const purchaseData = {
      uid: window.userData.id,
      secret: window.userData.secret,
      amount: amount,
    };

    const headers = { 'Content-Type': 'application/json' };
    if (window.userData && window.userData.secret) {
      headers['Authorization'] = `Bearer ${window.userData.secret}`;
      headers['X-API-Key'] = window.userData.secret;
    }

    fetch('/api/create-credits-checkout', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(purchaseData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success && data.url) {
          window.location.href = data.url;
        } else {
          toolsError('Failed to create checkout session:', data);
          if (typeof window.showToast === 'function') {
            window.showToast('Payment system temporarily unavailable. Please try again later.', { type: 'error' });
          } else {
            alert('Payment system temporarily unavailable. Please try again later.');
          }
        }
      })
      .catch((error) => {
        toolsError('Error purchasing credits:', error);
        if (typeof window.showToast === 'function') {
          window.showToast('Error processing purchase. Please try again.', { type: 'error' });
        } else {
          alert('Error processing purchase. Please try again.');
        }
      });
  }

  /**
   * Check if user has sufficient credits
   */
  NetwrckTools.checkCredits = function (requiredCredits, costInUSD) {
    toolsDebug(
      '[CREDITS] checkCredits called, userData:',
      JSON.stringify({
        secret: !!window.userData?.secret,
        free: window.userData?.free_credits,
        paid: window.userData?.paid_credits,
      })
    );
    if (window.userData && !window.userData.secret) {
      try {
        var ls = localStorage.getItem('userSecret');
        if (ls) window.userData.secret = ls;
        if (!window.userData.secret && document.cookie) {
          var m = document.cookie.match(/user_secret=([^;]+)/);
          if (m) window.userData.secret = m[1];
        }
      } catch (e) { /* ignore */ }
    }
    // First check if user is logged in at all
    if (
      !window.userData ||
      !window.userData.secret ||
      window.userData.secret === 'anonymous'
    ) {
      // Show login dialog first, NOT the add-user-dialog
      var loginDialog = document.getElementById('login-dialog');
      if (loginDialog && !loginDialog.open) {
        loginDialog.showModal();
      } else if (!loginDialog) {
        // Fallback to redirect if no login dialog
        window.location.href = '/login';
      }
      return false;
    }

    // Only check profile completion AFTER user is logged in
    // This could be triggered after successful login if needed
    // But should NOT be the first thing shown to anonymous users

    var totalCredits = (window.userData.free_credits || 0) + (window.userData.paid_credits || 0);
    if (!totalCredits && window.userData.credits) totalCredits = window.userData.credits;
    if (totalCredits < requiredCredits) {
      NetwrckTools.showCreditsToast(requiredCredits, costInUSD);
      return false;
    }

    return true;
  };

  NetwrckTools.handlePaymentRequired = function (requiredCredits, message) {
    if (typeof window.handleInsufficientCredits === 'function') {
      try {
        window.handleInsufficientCredits({ required: requiredCredits, message: message });
        return true;
      } catch { /* Error ignored */ }
    }
    if (typeof window.showTopupDialog === 'function') {
      try { window.showTopupDialog(); return true; } catch { /* Error ignored */ }
    }
    if (typeof requiredCredits === 'number' && requiredCredits > 0) {
      NetwrckTools.showCreditsToast(requiredCredits, (requiredCredits / 100).toFixed(2));
      return true;
    }
    NetwrckTools.showTopupCreditsModal();
    return true;
  };

  NetwrckTools.requireLogin = function () {
    // Hydrate secret from cookies/localStorage if missing
    if (window.userData && !window.userData.secret) {
      try {
        var ls = localStorage.getItem('userSecret');
        if (ls) window.userData.secret = ls;
        if (!window.userData.secret && document.cookie) {
          var m = document.cookie.match(/user_secret=([^;]+)/);
          if (m) window.userData.secret = m[1];
        }
      } catch (e) { /* ignore */ }
    }
    if (
      window.userData &&
      window.userData.secret &&
      window.userData.secret !== 'anonymous'
    ) {
      return true;
    }

    if (typeof window.showLoginDialog === 'function') {
      window.showLoginDialog(null, window.location.pathname + window.location.search);
    } else {
      var next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = '/login?next=' + next;
    }
    return false;
  };

  /**
   * Handle subscription check for non-subscribers
   */
  NetwrckTools.checkSubscription = function () {
    if (!window.userData.is_subscribed && window.userFullyLoaded) {
      if (window.subscribeImageGenCounter >= 1) {
        if (window.showSubscribeDialog && typeof window.showSubscribeDialog === 'function') {
          window.showSubscribeDialog();
        } else {
          try {
            var dlg = document.getElementById('subscribe-dialog');
            if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
          } catch { /* Error ignored */ }
        }
        if (typeof trackEvent === 'function') {
          trackEvent('user', 'paywall', 'tool');
        }
        return false;
      }
    }
    return true;
  };

  /**
   * Check if profile needs completion (ONLY call after login)
   */
  NetwrckTools.checkProfileCompletion = function () {
    // Only check if user is actually logged in
    if (
      !window.userData ||
      !window.userData.secret ||
      window.userData.secret === 'anonymous'
    ) {
      return false;
    }

    // Check if profile is incomplete (no username, description, etc)
    var needsProfile =
      !window.userData.username ||
      window.userData.username === window.userData.email ||
      !window.userData.description;

    if (needsProfile && !localStorage.getItem('profile_completion_skipped')) {
      var dialog = document.getElementById('add-user-dialog');
      if (dialog) {
        dialog.close();
        dialog.showModal();
      }
      return true;
    }
    return false;
  };

  /**
   * Setup common event handlers
   */
  function setupCommonEventHandlers() {
    // Listen for successful login/signup events
    $(document).on('user-logged-in user-signed-up', function () {
      // After successful login, check if profile needs completion
      setTimeout(function () {
        NetwrckTools.checkProfileCompletion();
      }, 500);
    });

    // Handle credit package selection
    $(document).on('change', 'input[name="credit-package"]', function () {
      const value = $(this).val();
      if (value === 'custom') {
        $('#custom-credits-container').show();
        if (typeof componentHandler !== 'undefined') {
          componentHandler.upgradeElements(
            $('#custom-credits-container .mdl-textfield').get()
          );
        }
      } else {
        $('#custom-credits-container').hide();
      }
    });

    // Update custom credits cost
    $(document).on('input', '#custom-credits-amount', function () {
      const amount = parseFloat($(this).val()) || 0;
      $('#custom-credits-cost').text(amount.toFixed(2));
    });

    // Handle checkout button from topup dialog
    $(document).on('click', '#checkout-credits-btn', function () {
      let amount;
      const selectedPackage = $('input[name="credit-package"]:checked').val();

      if (selectedPackage === 'custom') {
        amount = parseFloat($('#custom-credits-amount').val()) || 0;
        if (amount < 1) {
          if (typeof window.showToast === 'function') {
            window.showToast('Please enter a valid amount', { type: 'warning' });
          } else {
            alert('Please enter a valid amount');
          }
          return;
        }
      } else {
        amount = parseFloat(selectedPackage);
      }

      NetwrckTools.purchaseCredits(amount);
    });

    // Handle dialog close buttons
    $(document).on('click', '#topup-credits-dialog .close', function () {
      document.getElementById('topup-credits-dialog').close();
    });

    // Listen for user updates
    $(document).on('user-ready', function (evt, user) {
      if (user && user.free_credits !== undefined) {
        window.userData = user;
        updateBalanceDisplay();
      }
    });

    // Handle paste events for image uploads
    document.addEventListener('paste', function (event) {
      try {
        const items = (event.clipboardData || event.originalEvent.clipboardData)
          .items;
        for (let index in items) {
          const item = items[index];
          if (item.kind === 'file') {
            const blob = item.getAsFile();
            if (blob && blob.type && blob.type.startsWith('image/')) {
              // Trigger custom event for tools to handle
              const customEvent = new CustomEvent('imageFilePasted', {
                detail: { file: blob },
              });
              document.dispatchEvent(customEvent);
              // Also trigger a jQuery event for legacy handlers
              if (typeof $ !== 'undefined') {
                $(document).trigger('tool-image-pasted', [blob]);
              }
            }
          }
        }
      } catch (err) {
        toolsWarn('Paste handler failed:', err);
      }
    });

    // Listen for storage events for cross-tab updates
    window.addEventListener('storage', function (e) {
      if (e.key === 'netwrck_user' && e.newValue) {
        try {
          var newUserData = JSON.parse(e.newValue);
          if (
            newUserData &&
            newUserData.secret &&
            newUserData.secret !== 'anonymous'
          ) {
            window.userData = newUserData;
            updateBalanceDisplay();
          }
        } catch (err) {
          toolsError('Error parsing user data from storage:', err);
        }
      }
    });
  }

  /**
   * Utility function to show loading progress
   */
  NetwrckTools.showLoading = function (elementId) {
    const element = document.getElementById(elementId);
    if (element) element.style.display = 'block';
  };

  /**
   * Utility function to hide loading progress
   */
  NetwrckTools.hideLoading = function (elementId) {
    const element = document.getElementById(elementId);
    if (element) element.style.display = 'none';
  };

  /**
   * Common file upload handler
   */
  NetwrckTools.handleFileUpload = function (file, callback) {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function (e) {
      if (typeof callback === 'function') {
        callback(e.target.result, file);
      }
    };
    reader.readAsDataURL(file);
  };

  /**
   * Show notification using MDL snackbar/toast
   */
  NetwrckTools.showNotification = function(message, type = 'info', actionCallback = null) {
    if (!document.getElementById('netwrck-toast-style')) {
      const style = document.createElement('style');
      style.id = 'netwrck-toast-style';
      style.textContent = '#toast.netwrck-toast:not(.mdl-snackbar--active){opacity:0;visibility:hidden;pointer-events:none}#toast.netwrck-toast{transition:opacity .25s ease,visibility .25s ease}#toast.netwrck-toast .mdl-snackbar__action[aria-hidden="true"],#toast.netwrck-toast .mdl-snackbar__action:empty{display:none}';
      document.head.appendChild(style);
    }
    let toastContainer = document.getElementById('toast');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'toast';
      toastContainer.className = 'mdl-js-snackbar mdl-snackbar netwrck-toast';
      toastContainer.innerHTML = '<div class="mdl-snackbar__text"></div><button class="mdl-snackbar__action" type="button"></button>';
      document.body.appendChild(toastContainer);
      if (typeof componentHandler !== 'undefined') {
        componentHandler.upgradeElement(toastContainer);
      }
    }
    toastContainer.classList.add('netwrck-toast');
    toastContainer.classList.remove('netwrck-toast--error', 'netwrck-toast--warning', 'netwrck-toast--success', 'netwrck-toast--info');
    toastContainer.classList.add('netwrck-toast--' + (type || 'info'));

    const colorMap = {
      'error': '#dc2626',
      'warning': '#f97316',
      'success': '#16a34a',
      'info': '#2563eb'
    };
    const accent = colorMap[type] || colorMap.info;
    toastContainer.style.cssText = [
      'background:#111827',
      'border:1px solid rgba(255,255,255,0.14)',
      'border-left:5px solid ' + accent,
      'border-radius:10px',
      'box-shadow:0 18px 42px rgba(15,23,42,0.28)',
      'left:24px',
      'right:auto',
      'bottom:24px',
      'min-width:320px',
      'max-width:min(560px, calc(100vw - 32px))',
      'padding:0',
      'text-align:left',
      'z-index:2147483647'
    ].join(';');
    const textEl = toastContainer.querySelector('.mdl-snackbar__text');
    if (textEl) {
      textEl.style.cssText = [
        'color:#fff',
        'font:600 14px/1.45 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        'letter-spacing:0',
        'padding:14px 18px',
        'text-align:left',
        'white-space:normal',
        'word-break:break-word'
      ].join(';');
    }
    const actionEl = toastContainer.querySelector('.mdl-snackbar__action');
    if (actionEl) {
      actionEl.style.cssText = [
        'color:#fff',
        'font-weight:800',
        'padding:0 16px',
        'min-width:auto'
      ].join(';');
    }

    let hasTopupAction = false;
    if (message.toLowerCase().includes('insufficient funds')) {
      hasTopupAction = true;
      actionCallback = function() {
        if (typeof openTopupCreditsDialog === 'function') {
          openTopupCreditsDialog();
        } else if (typeof NetwrckTools.showTopupCreditsModal === 'function') {
          NetwrckTools.showTopupCreditsModal();
        } else {
          window.location.href = '/account/credits';
        }
      };
    }

    if (toastContainer && toastContainer.MaterialSnackbar) {
      const hasAction = hasTopupAction || typeof actionCallback === 'function';
      const actionText = hasTopupAction ? 'TOP UP' : (hasAction ? 'ACTION' : '');
      const data = {
        message: message,
        timeout: type === 'error' ? 5000 : (hasTopupAction ? 6000 : 3000),
        actionHandler: actionText ? (actionCallback || function() {}) : undefined,
        actionText: actionText
      };
      if (colorMap[type]) {
        toastContainer.style.setProperty('--toast-accent', colorMap[type]);
        setTimeout(() => {
          toastContainer.style.removeProperty('--toast-accent');
        }, data.timeout + 500);
      }
      toastContainer.MaterialSnackbar.showSnackbar(data);
    } else {
      toolsDebug(`[${type.toUpperCase()}] ${message}`);
    }
  };

  // Prompt History
  (function() {
    var MAX = 100;
    var states = new WeakMap();

    function key(el) { return 'prompt_history_' + (el.id || el.name || 'default'); }
    function load(el) { try { return JSON.parse(localStorage.getItem(key(el))) || []; } catch(e) { return []; } }
    function save(el, h) { try { localStorage.setItem(key(el), JSON.stringify(h.slice(-MAX))); } catch(e) {} }
    function st(el) { if (!states.has(el)) states.set(el, {idx:-1, draft:''}); return states.get(el); }

    function getDeleteBtn(el) {
      var c = el.closest('.mdl-textfield') || el.parentElement;
      var btn = c.querySelector('.prompt-history-del');
      if (btn) return btn;
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'prompt-history-del';
      btn.innerHTML = '&times;';
      btn.title = 'Remove from history';
      btn.style.cssText = 'position:absolute;right:4px;top:50%;transform:translateY(-50%);background:rgba(0,0,0,0.06);border:none;font-size:20px;cursor:pointer;color:#888;padding:0 6px;line-height:1;display:none;z-index:10;border-radius:3px;';
      if (getComputedStyle(c).position === 'static') c.style.position = 'relative';
      c.appendChild(btn);
      btn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        var s = st(el), h = load(el);
        if (s.idx >= 0 && s.idx < h.length) {
          h.splice(s.idx, 1);
          save(el, h);
          if (!h.length) { el.value = s.draft; s.idx = -1; btn.style.display = 'none'; }
          else { if (s.idx >= h.length) s.idx = h.length - 1; el.value = h[s.idx]; }
        }
      });
      return btn;
    }

    function onKey(e) {
      var el = e.target, s = st(el), h, btn;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        h = load(el);
        if (!h.length) return;
        e.preventDefault();
        btn = getDeleteBtn(el);
        if (e.key === 'ArrowUp') {
          if (s.idx === -1) { s.draft = el.value; s.idx = h.length - 1; }
          else if (s.idx > 0) s.idx--;
          el.value = h[s.idx]; btn.style.display = 'block';
        } else {
          if (s.idx === -1) return;
          if (s.idx < h.length - 1) { s.idx++; el.value = h[s.idx]; btn.style.display = 'block'; }
          else { s.idx = -1; el.value = s.draft; btn.style.display = 'none'; }
        }
      } else if (e.key !== 'Enter') {
        s.idx = -1;
        btn = (el.closest('.mdl-textfield') || el.parentElement).querySelector('.prompt-history-del');
        if (btn) btn.style.display = 'none';
      }
    }

    NetwrckTools.initPromptHistory = function(el) {
      if (el._phInit) return;
      el._phInit = true;
      el.addEventListener('keydown', onKey);
    };

    NetwrckTools.saveToPromptHistory = function(elOrId, prompt) {
      var el = typeof elOrId === 'string' ? document.getElementById(elOrId) : elOrId;
      if (!el || !prompt || !prompt.trim()) return;
      prompt = prompt.trim();
      var h = load(el);
      var i = h.indexOf(prompt);
      if (i !== -1) h.splice(i, 1);
      h.push(prompt);
      save(el, h);
      var s = st(el); s.idx = -1; s.draft = '';
      var btn = (el.closest('.mdl-textfield') || el.parentElement).querySelector('.prompt-history-del');
      if (btn) btn.style.display = 'none';
    };

    function autoInit() {
      var els = document.querySelectorAll('.createaiimageprompt, .createaivideoprompt, [data-prompt-history]');
      for (var i = 0; i < els.length; i++) NetwrckTools.initPromptHistory(els[i]);
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' && e.target._phInit && e.target.value && e.target.value.trim()) {
          NetwrckTools.saveToPromptHistory(e.target, e.target.value);
        }
      }, true);
      document.addEventListener('click', function(e) {
        var btn = e.target.closest('.make-art-button, [class*="create-ai-image-button"], [class*="create-ai-video-button"]');
        if (!btn) return;
        var container = btn.closest('.main-game-wrapper, .demo-content, body') || document;
        var input = container.querySelector('.createaiimageprompt, .createaivideoprompt, [data-prompt-history]');
        if (input && input.value && input.value.trim()) NetwrckTools.saveToPromptHistory(input, input.value);
      }, true);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
    else setTimeout(autoInit, 0);
  })();

  // Make globally accessible
  window.showTopupCreditsModal = NetwrckTools.showTopupCreditsModal;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initializeCommonFeatures();
      // Ensure MDL components are upgraded
      if (typeof componentHandler !== 'undefined') {
        componentHandler.upgradeDom();
      }
    });
  } else {
    initializeCommonFeatures();
    // Ensure MDL components are upgraded
    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeDom();
    }
  }
  
  // Auto-upgrade MDL components after dynamic content changes
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      let shouldUpgrade = false;
      for (let mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (let node of mutation.addedNodes) {
            if (node.nodeType === 1 && node.className && node.className.indexOf && node.className.indexOf('mdl-') !== -1) {
              shouldUpgrade = true;
              break;
            }
          }
        }
      }
      if (shouldUpgrade && typeof componentHandler !== 'undefined') {
        setTimeout(function() {
          componentHandler.upgradeDom();
        }, 10);
      }
    });
    
    // Start observing when DOM is ready
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        observer.observe(document.body, { childList: true, subtree: true });
      });
    }
  }
})();
